import {
  Box,
  Typography,
  Avatar,
  Button,
  Modal,
  TextField,
  Switch,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Stack,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import {
  PhoneMissed,
  AddIcCall,
  Close as CloseIcon,
  CheckCircleOutline,
  SaveAlt,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { capitalize } from "../../../config/settings.config";

const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const ANSWERED_COLOR = "#16a34a";
const MISSED_COLOR = "#dc2626";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(() => ({
  width: 42,
  height: 26,
  padding: 0,

  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: ANSWERED_COLOR,
        opacity: 1,
      },
    },
    "&:not(.Mui-checked)": {
      "& + .MuiSwitch-track": {
        backgroundColor: MISSED_COLOR,
        opacity: 1,
      },
    },
  },

  "& .MuiSwitch-thumb": {
    width: 22,
    height: 22,
    borderRadius: 11,
    boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
  },

  "& .MuiSwitch-track": {
    borderRadius: 13,
    opacity: 1,
  },
}));

const AddCallModal = ({
  open,
  onClose,
  theme,
  notes,
  onNotesChange,
  rnr,
  onRnrChange,
  callSource,
  onCallSourceChange,
  durationMinutes,
  onDurationMinutesChange,
  durationSeconds,
  onDurationSecondsChange,
  saving,
  onSubmit,
}) => {
  // Notes validation: required when Answered, not required when RNR
  const isNotesValid = rnr || notes.trim().length > 0;

  const handleClose = () => {
    onClose();
    onNotesChange("");
    onRnrChange(false);
    onDurationMinutesChange("");
    onDurationSecondsChange("");
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <MotionBox
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        sx={{
          width: { xs: "92%", sm: 460 },
          maxWidth: 460,
          bgcolor: "#FFFFFF",
          borderRadius: 10,
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
          outline: "none",
          display: "flex",
          flexDirection: "column",
          m: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            p: 3,
            pb: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                width: 56,
                height: 56,
                borderRadius: 50,
              }}
            >
              <AddIcCall sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, color: "#1E293B" }}
              >
                Add Call Log
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748B" }}>
                Record a new call or missed call details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              width: 36,
              height: 36,
              color: "#94A3B8",
              "&:hover": {
                bgcolor: "#F1F5F9",
                color: "#64748B",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* Answered Toggle - Moved to top, directly below header */}
          <Box
            sx={{
              width: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderRadius: 3,
              bgcolor: rnr
                ? "rgba(239, 68, 68, 0.06)"
                : "rgba(16, 185, 129, 0.06)",
              border: "1px solid",
              borderColor: rnr
                ? "rgba(239, 68, 68, 0.15)"
                : "rgba(16, 185, 129, 0.15)",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: rnr
                    ? "rgba(239, 68, 68, 0.12)"
                    : "rgba(16, 185, 129, 0.12)",
                  color: rnr ? "#EF4444" : "#10B981",
                }}
              >
                {rnr ? (
                  <PhoneMissed sx={{ fontSize: 20 }} />
                ) : (
                  <CheckCircleOutline sx={{ fontSize: 20 }} />
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: rnr ? "#EF4444" : "#10B981",
                }}
              >
                {rnr ? "Missed Call (RNR)" : "Answered"}
              </Typography>
            </Stack>
            <IOSSwitch checked={!rnr} onChange={() => onRnrChange(!rnr)} />
          </Box>

          {/* Conditional Fields - Only show when Answered (not RNR) */}
          {!rnr && (
            <>
              {/* Notes Field */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mb: 1 }}
                >
                  Notes <span style={{ color: "#EF4444" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Brief summary of the conversation..."
                  value={notes}
                  onChange={(e) => onNotesChange(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 5,
                      bgcolor: "#FFFFFF",
                      fontSize: 14,
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 1.5,
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#94A3B8",
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {/* Call Duration */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#1E293B", mb: 1 }}
                >
                  Call Duration
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="00"
                      value={durationMinutes}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (
                          val === "" ||
                          (parseInt(val) >= 0 && parseInt(val) <= 999)
                        ) {
                          onDurationMinutesChange(val);
                        }
                      }}
                      inputProps={{ min: 0, max: 999 }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#94A3B8",
                              ml: 1,
                            }}
                          >
                            MIN
                          </Typography>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 4,
                          bgcolor: "#FFFFFF",
                          "& fieldset": { borderColor: "#E2E8F0" },
                          "&:hover fieldset": { borderColor: "#CBD5E1" },
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1.5,
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="00"
                      value={durationSeconds}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (
                          val === "" ||
                          (parseInt(val) >= 0 && parseInt(val) <= 59)
                        ) {
                          onDurationSecondsChange(val);
                        }
                      }}
                      inputProps={{ min: 0, max: 59 }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#94A3B8",
                              ml: 1,
                            }}
                          >
                            SEC
                          </Typography>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 4,
                          bgcolor: "#FFFFFF",
                          "& fieldset": { borderColor: "#E2E8F0" },
                          "&:hover fieldset": { borderColor: "#CBD5E1" },
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1.5,
                          },
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Call Type */}
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1E293B",
                    mb: 1,
                  }}
                >
                  Call Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={callSource}
                    onChange={(e) => onCallSourceChange(e.target.value)}
                    sx={{
                      borderRadius: 4,
                      bgcolor: "#FFFFFF",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 1.5,
                      },
                    }}
                  >
                    <MenuItem value="voicemail">Voicemail</MenuItem>
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </Box>

        {/* Footer Buttons */}
        <Box sx={{ p: 3, pt: 2, display: "flex", gap: 2 }}>
          <Button
            fullWidth
            onClick={handleClose}
            sx={{
              borderRadius: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: 14,
              color: "#64748B",
              bgcolor: "#F1F5F9",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#E2E8F0",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={!isNotesValid || saving}
            onClick={onSubmit}
            startIcon={<SaveAlt sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: 14,
              color: "#FFFFFF",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              boxShadow: "0 4px 14px rgba(124, 58, 237, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 6px 20px rgba(124, 58, 237, 0.4)",
              },
              "&:disabled": {
                background: "#E2E8F0",
                color: "#94A3B8",
                boxShadow: "none",
              },
            }}
          >
            {saving ? "Saving..." : "Save Log"}
          </Button>
        </Box>
      </MotionBox>
    </Modal>
  );
};

export default AddCallModal;
